(() => {
  /* Cart Drawer V4 owns its protection lifecycle through native section
     responses. Keep this legacy reconciler (and its fetch wrapper/polling)
     completely out of the V4 path. */
  if (document.querySelector('[data-ao-cart-v4]')) return;

  const root = window.Shopify?.routes?.root || '/';
  const cartUrl = `${root}cart.js`;
  const changeUrl = `${root}cart/change.js`;
  const nativeFetch = window.fetch.bind(window);
  let timer;
  let releaseTimer;
  let statusTimer;
  let running = false;
  let cartMutationInFlight = 0;
  let cartMutationRevision = 0;
  let rerunRequested = false;
  let pendingAction = '';
  let lastStatus = '';
  let restoreProtectionFocus = false;

  if (window.aocdxProtectionGuardReady) return;
  window.aocdxProtectionGuardReady = true;

  const requestUrl = (input) => {
    if (typeof input === 'string') return input;
    if (input instanceof URL) return input.href;
    return input?.url || '';
  };

  const isCartMutation = (input) => /\/cart\/(add|change|update|clear)(\.js)?(?:\?|$)/.test(requestUrl(input));

  const protectionHosts = () => document.querySelectorAll('[data-aocdx-protection="true"]');

  const protectionVariantIds = () => new Set(
    Array.from(protectionHosts())
      .map((host) => Number(host.dataset.id))
      .filter(Number.isFinite)
  );

  const isTargetProtectionItem = (item) => protectionVariantIds().has(Number(item?.variant_id));

  const isProtectionItem = (item) => (
    item?.handle === 'shipping-protection'
    || isTargetProtectionItem(item)
  );

  const getProtectionItems = (cart) => (
    Array.isArray(cart?.items)
      ? cart.items.filter(isProtectionItem)
      : []
  );

  const setStatus = (message) => {
    lastStatus = message;
    protectionHosts().forEach((host) => {
      const status = host.querySelector('[data-aocdx-protection-status]');
      if (status && status.textContent !== message) status.textContent = message;
    });
    window.clearTimeout(statusTimer);
    if (message && message !== 'Unable to update. Please try again.') {
      statusTimer = window.setTimeout(() => setStatus(''), 2400);
    }
  };

  const syncUI = (cart, message = lastStatus) => {
    const selected = getProtectionItems(cart).some((item) => (
      isTargetProtectionItem(item) && Number(item.quantity || 0) > 0
    ));
    protectionHosts().forEach((host) => {
      if (host.dataset.selected !== String(selected)) host.dataset.selected = String(selected);
      host.removeAttribute('aria-busy');
      const control = host.querySelector('[data-aocdx-protection-switch]');
      if (control) {
        if (control.getAttribute('aria-checked') !== String(selected)) {
          control.setAttribute('aria-checked', String(selected));
        }
        control.disabled = false;
      }
      const status = host.querySelector('[data-aocdx-protection-status]');
      if (status && status.textContent !== message) status.textContent = message;
    });
    if (restoreProtectionFocus) {
      document.querySelector('[data-aocdx-protection-switch]')?.focus({ preventScroll: true });
      restoreProtectionFocus = false;
    }
    window.clearTimeout(releaseTimer);
    return selected;
  };

  const sectionIds = () => {
    const drawer = document.querySelector('cart-drawer');
    const cartItems = document.querySelector('cart-drawer-items');
    const sectionSource = cartItems && typeof cartItems.getSectionsToRender === 'function' ? cartItems : drawer;
    if (sectionSource && typeof sectionSource.getSectionsToRender === 'function') {
      const ids = sectionSource.getSectionsToRender().map((entry) => entry.section || entry.id).filter(Boolean);
      if (ids.length) return ids;
    }
    return ['cart-drawer', 'cart-icon-bubble'];
  };

  const updateLine = (key, quantity) => nativeFetch(changeUrl, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      id: key,
      quantity,
      sections: sectionIds(),
      sections_url: window.location.pathname
    })
  });

  const renderCartSections = (state) => {
    const drawer = document.querySelector('cart-drawer');
    if (drawer && state?.sections && typeof drawer.renderContents === 'function') {
      drawer.renderContents(state, true);
    }
  };

  const fetchCart = async () => {
    const response = await nativeFetch(cartUrl, {
      cache: 'no-store',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error('Cart could not be read.');
    return response.json();
  };

  const reconcile = async () => {
    if (cartMutationInFlight > 0) {
      schedule(140);
      return;
    }
    if (running) {
      rerunRequested = true;
      return;
    }
    running = true;
    const startingRevision = cartMutationRevision;

    try {
      let cart = await fetchCart();
      if (cartMutationInFlight > 0 || cartMutationRevision !== startingRevision) {
        rerunRequested = true;
        return;
      }
      let items = Array.isArray(cart.items) ? cart.items : [];
      let protection = getProtectionItems(cart);
      const hasShippableItem = items.some((item) => (
        !isProtectionItem(item)
        && item.requires_shipping === true
        && Number(item.quantity || 0) > 0
      ));

      const changes = [];
      if (!hasShippableItem) {
        protection.forEach((item) => changes.push([item.key, 0]));
      } else {
        let targetProtectionKept = false;
        protection.forEach((item) => {
          const isExactTarget = isTargetProtectionItem(item);
          const expectedQuantity = isExactTarget && targetProtectionKept == false ? 1 : 0;
          if (isExactTarget) targetProtectionKept = true;
          if (Number(item.quantity || 0) !== expectedQuantity) changes.push([item.key, expectedQuantity]);
        });
      }

      let finalState;
      for (const [key, quantity] of changes) {
        const changeResponse = await updateLine(key, quantity);
        if (!changeResponse.ok) throw new Error('Protection quantity could not be corrected.');
        finalState = await changeResponse.json();
      }

      if (finalState) {
        renderCartSections(finalState);
        cart = finalState;
        await new Promise((resolve) => window.setTimeout(resolve, 80));
      }

      if (cartMutationInFlight > 0 || cartMutationRevision !== startingRevision) {
        rerunRequested = true;
        return;
      }

      const selected = getProtectionItems(cart).some((item) => (
        isTargetProtectionItem(item) && Number(item.quantity || 0) > 0
      ));
      let message = lastStatus;
      if (pendingAction === 'add') message = selected ? 'Protection added' : 'Unable to update. Please try again.';
      if (pendingAction === 'remove') message = selected ? 'Unable to update. Please try again.' : 'Protection removed';
      pendingAction = '';
      setStatus(message);
      syncUI(cart, message);
      document.dispatchEvent(new CustomEvent('aocdx:cart-updated', { detail: { cart } }));

      if (changes.length) document.dispatchEvent(new CustomEvent('aocdx:protection-reconciled'));
      if (changes.length && /\/cart\/?$/.test(window.location.pathname)) window.location.reload();
    } catch (_error) {
      pendingAction = '';
      setStatus('Unable to update. Please try again.');
      try {
        syncUI(await fetchCart(), lastStatus);
      } catch (_syncError) {
        protectionHosts().forEach((host) => {
          host.removeAttribute('aria-busy');
          const control = host.querySelector('[data-aocdx-protection-switch]');
          if (control) control.disabled = false;
        });
      }
    } finally {
      running = false;
      if (rerunRequested) {
        rerunRequested = false;
        schedule(0);
      }
    }
  };

  const schedule = (delay = 320) => {
    window.clearTimeout(timer);
    timer = window.setTimeout(reconcile, delay);
  };

  window.fetch = (...args) => {
    const cartMutation = isCartMutation(args[0]);
    if (cartMutation) {
      cartMutationInFlight += 1;
      cartMutationRevision += 1;
    }
    const response = nativeFetch(...args);
    if (cartMutation) {
      response.finally(() => {
        cartMutationInFlight = Math.max(0, cartMutationInFlight - 1);
        schedule();
      }).catch(() => {});
    }
    return response;
  };

  const observeCart = () => {
    const drawers = document.querySelectorAll('cart-drawer');
    if (!drawers.length || !('MutationObserver' in window)) return;
    const observer = new MutationObserver((records) => {
      const relevant = records.some((record) => {
        const target = record.target instanceof Element ? record.target : record.target.parentElement;
        return !target?.closest?.('[data-aocdx-protection-status]');
      });
      if (relevant) schedule(180);
    });
    drawers.forEach((drawer) => {
      observer.observe(drawer, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'data-selected']
      });
    });
  };

  document.addEventListener('DOMContentLoaded', () => {
    observeCart();
    document.addEventListener('keydown', (event) => {
      const protectionSwitch = event.target.closest?.('[data-aocdx-protection-switch]');
      if (!protectionSwitch || !['Enter', ' '].includes(event.key)) return;
      event.preventDefault();
      if (event.repeat || protectionSwitch.disabled) return;
      protectionSwitch.click();
    });
    document.addEventListener('click', (event) => {
      const protectionSwitch = event.target.closest('[data-aocdx-protection-switch]');
      if (protectionSwitch) {
        protectionSwitch.dataset.aocdxPreviousChecked = protectionSwitch.getAttribute('aria-checked') || 'false';
      }
    }, true);
    document.addEventListener('click', (event) => {
      const protectionSwitch = event.target.closest('[data-aocdx-protection-switch]');
      if (protectionSwitch) {
        restoreProtectionFocus = true;
        pendingAction = protectionSwitch.dataset.aocdxPreviousChecked === 'true' ? 'remove' : 'add';
        delete protectionSwitch.dataset.aocdxPreviousChecked;
        const host = protectionSwitch.closest('[data-aocdx-protection="true"]');
        if (host?.dataset.selected) {
          protectionSwitch.setAttribute('aria-checked', host.dataset.selected);
        }
        protectionSwitch.disabled = true;
        host?.setAttribute('aria-busy', 'true');
        window.clearTimeout(releaseTimer);
        releaseTimer = window.setTimeout(() => schedule(0), 5000);
        return;
      }

      if (event.target.closest('cart-remove-button, .quantity__button, .upsell-toggle-btn, .upsell__plus-btn')) {
        schedule(700);
      }
    });
    schedule(0);
  }, { once: true });

  window.addEventListener('pageshow', () => schedule(0));
})();
